/**
 * Unified Asset Configuration System
 *
 * This file consolidates all asset management (advert images, profile images, backgrounds)
 * into a single, organization-aware system. It replaces:
 * - src/config/advertImages.ts
 * - src/config/profileImages.ts
 * - src/config/backgrounds.ts
 *
 * Features:
 * - Organization-specific asset packs (school, church, organization)
 * - Shared assets available to all organization types
 * - Cross-functional: advert images can be used as backgrounds
 * - Automatic fallback to shared assets if org-specific don't exist
 */

export type OrganizationType =
  | "school"
  | "church"
  | "organization"
  | "individual"
  | string
  | undefined;

export const DEFAULT_NOTICE_BACKGROUND = "/assets/shared/backgrounds/school-corridor.webp";

export interface BackgroundOption {
  label: string;
  value: string;
}

// Shared assets - available to all organization types
const SHARED_ADVERT_IMAGES: string[] = [
  // Shared advert images (currently empty - all adverts are org-specific)
];

const SHARED_BACKGROUND_IMAGES: string[] = [
  "teal-classroom-desk.webp",
  "golden-school-hall.webp",
  "leafy-school-courtyard.webp",
  "classroom-chairs.webp",
  "window-side-books.webp",
  "birthday-party.webp",
  "birthday-sparkle.webp",
  "birthday-cake.webp",
  "graduation-celebration.webp",
  "achievement-star.webp",
  "graduation-books.webp",
  "prom-disco.webp",
  "prom-champagne.webp",
  "prom-gala.webp",
  "prom-garden-lights.webp",
  "prom-neon.webp",

  "school-backpack.webp",
  "sunset-school-field.webp",
  "school-library-desk.webp",

  "sunlit-classroom.webp",
  "teal-pencil-desk.webp",
  "window-light.webp",
  "school-garden.webp",
  "classroom-books.webp",
  "school-corridor.webp",
  "sunlit-study.webp",
  "blue-sky-campus.webp",

  "golden-curve.webp",
  "coastal-arch.webp",
  "ocean-terrace.webp",
  "garden-courtyard.webp",
  "rugby-sunset.webp",

  "warm-ivory.webp",
  "coastal-sunset.webp",
  "sage-cream.webp",
  "a.webp",
  "b.webp",
  "c.webp",
  "d.webp",
  "e.webp",
  "f.webp",
  "g.webp",
  "h.webp",
  "i.webp",
  "j.webp",
  "k.webp",
  "l.webp",
  "m.webp",
  "n.webp",
  "p.webp",
];

const CLEAN_BACKGROUND_LABELS: Record<string, string> = {
  "prom-disco.webp": "Disco Night",
  "prom-champagne.webp": "Champagne Celebration",
  "prom-gala.webp": "Black & Gold Gala",
  "prom-garden-lights.webp": "Garden Party Lights",
  "prom-neon.webp": "Neon Night",

  "graduation-celebration.webp": "Graduation Celebration",
  "achievement-star.webp": "Achievement Star",
  "graduation-books.webp": "Graduation Books",
  "birthday-party.webp": "Birthday Party",
  "birthday-sparkle.webp": "Birthday Sparkle",
  "birthday-cake.webp": "Birthday Cake",
  "ballet-v2.webp": "Ballet",
  "dance-v2.webp": "Dance",
  "daycare-v2.webp": "Daycare",
  "important-v2.webp": "Important update",
  "karate-v2.webp": "Karate",
  "lift-v2.webp": "Lift club",
  "math-v2.webp": "Math tuition",
  "speech-v2.webp": "Speech and drama",
  "sports-v2.webp": "Sports gear",
  "swimming-v2.webp": "Swimming",
  "textbooks-v2.webp": "Textbooks",
  "uniforms-v2.webp": "Uniforms",

  "teal-classroom-desk.webp": "Teal Classroom Desk",
  "golden-school-hall.webp": "Golden School Hall",
  "leafy-school-courtyard.webp": "Leafy School Courtyard",
  "classroom-chairs.webp": "Classroom Chairs",
  "window-side-books.webp": "Window-side Books",
  "school-backpack.webp": "School Backpack",
  "sunset-school-field.webp": "Sunset School Field",
  "school-library-desk.webp": "School Library Desk",

  "sunlit-classroom.webp": "Sunlit Classroom",
  "teal-pencil-desk.webp": "Teal Pencil Desk",
  "window-light.webp": "Window Light",
  "school-garden.webp": "School Garden",
  "classroom-books.webp": "Classroom Books",
  "school-corridor.webp": "School Corridor",
  "sunlit-study.webp": "Sunlit Study",
  "blue-sky-campus.webp": "Blue Sky Campus",

  "golden-curve.webp": "Golden Curve",
  "coastal-arch.webp": "Coastal Arch",
  "ocean-terrace.webp": "Ocean Terrace",
  "garden-courtyard.webp": "Garden Courtyard",
  "rugby-sunset.webp": "Rugby at Sunset",

  "warm-ivory.webp": "Warm Ivory",
  "coastal-sunset.webp": "Coastal Sunset",
  "sage-cream.webp": "Sage & Cream",
};

// Organization-specific assets
const ORGANIZATION_ADVERT_IMAGES: Record<string, string[]> = {
  // Versioned assets keep previously sent notices at their original URLs.
  school: [
    "ballet-v2.webp",
    "dance-v2.webp",
    "daycare-v2.webp",
    "important-v2.webp",
    "karate-v2.webp",
    "lift-v2.webp",
    "math-v2.webp",
    "speech-v2.webp",
    "sports-v2.webp",
    "swimming-v2.webp",
    "textbooks-v2.webp",
    "uniforms-v2.webp",
  ],
  church: [
    // Church-specific advert images can be added here
    // Example: "service.png", "choir.png"
  ],
  organization: [
    // Organization-specific advert images can be added here
  ],
};

const ORGANIZATION_BACKGROUND_IMAGES: Record<string, string[]> = {
  school: [
    "sa.jpg",
    "sb.jpg",
    "sc.jpg",
    "sd.jpg",
    "se.jpg",
    "sf.jpg",
    "sg.webp",
    "sh.webp",
    "si.webp",
    "sj.webp",
    "sk.jpg",
    "sl.jpg",
    "sm.jpg",
    "sn.webp",
  ],
  church: [
    // Church-specific background images can be added here
    // Example: "chapel.jpg", "cross.jpg"
    "ca.webp",
    "cb.webp",
    "cc.webp",
    "cd.webp",
  ],
  organization: [
    // Organization-specific background images can be added here
  ],
};

/**
 * Normalize organization type to a valid key
 */
function normalizeOrgType(orgType?: OrganizationType): string | null {
  if (!orgType) return null;
  const normalized = orgType.toLowerCase().trim();
  if (["school", "church", "organization", "individual"].includes(normalized)) {
    return normalized;
  }
  return null;
}

/**
 * Get advert images for an organization type
 * Merges shared + organization-specific assets
 */
export function getAdvertImages(orgType?: OrganizationType): string[] {
  const normalizedType = normalizeOrgType(orgType);
  const orgImages = normalizedType
    ? ORGANIZATION_ADVERT_IMAGES[normalizedType] || []
    : [];

  // Merge shared + org-specific, removing duplicates
  const allImages = Array.from(
    new Set([...SHARED_ADVERT_IMAGES, ...orgImages]),
  );

  // Return full paths
  return allImages.map((filename) => {
    // Check if org-specific image exists, otherwise use shared
    if (normalizedType && orgImages.includes(filename)) {
      return `/assets/${normalizedType}/adverts/${filename}`;
    }
    return `/assets/shared/adverts/${filename}`;
  });
}

/**
 * Get background images for an organization type
 * Merges shared + organization-specific assets
 */
export function getBackgroundImages(orgType?: OrganizationType): string[] {
  const normalizedType = normalizeOrgType(orgType);
  const orgImages = normalizedType
    ? ORGANIZATION_BACKGROUND_IMAGES[normalizedType] || []
    : [];

  // Merge shared + org-specific, removing duplicates
  const allImages = Array.from(
    new Set([...SHARED_BACKGROUND_IMAGES, ...orgImages]),
  );

  // Return full paths
  return allImages.map((filename) => {
    // Check if org-specific image exists, otherwise use shared
    if (normalizedType && orgImages.includes(filename)) {
      return `/assets/${normalizedType}/backgrounds/${filename}`;
    }
    return `/assets/shared/backgrounds/${filename}`;
  });
}

/**
 * Get profile images for an organization type
 * Currently uses background images (same as old profileImages.ts)
 * Can be customized per org type in the future
 */
export function getProfileImages(orgType?: OrganizationType): string[] {
  // Profile images use the same images as backgrounds
  return getBackgroundImages(orgType);
}

/**
 * Get background options for notice/event backgrounds
 * Returns BackgroundOption[] format with labels
 * Includes both background images AND advert images (cross-functional)
 */
export function getAllBackgroundOptions(
  orgType?: OrganizationType,
): BackgroundOption[] {
  const options: BackgroundOption[] = [{ label: "No Background", value: "" }];

  // Add background images
  const backgroundImages = getBackgroundImages(orgType);
  backgroundImages.forEach((path, index) => {
    const filename = path.split("/").pop() || "";
    const letter = filename.split(".")[0].toUpperCase();
    options.push({
      label: CLEAN_BACKGROUND_LABELS[filename] || `Background ${letter}`,
      value: path,
    });
  });

  // Add advert images as background options (cross-functional)
  const advertImages = getAdvertImages(orgType);
  advertImages.forEach((path) => {
    const filename = path.split("/").pop() || "";
    const name = filename.split(".")[0].replace(/-/g, " ");
    // Capitalize first letter of each word
    const label = name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    options.push({
      label: CLEAN_BACKGROUND_LABELS[filename] || label,
      value: path,
    });
  });

  return options;
}

/**
 * Get background options (backgrounds only, no adverts)
 * For backwards compatibility with existing code
 */
export function getBackgroundOptions(
  orgType?: OrganizationType,
): BackgroundOption[] {
  const options: BackgroundOption[] = [{ label: "No Background", value: "" }];

  const backgroundImages = getBackgroundImages(orgType);
  backgroundImages.forEach((path) => {
    const filename = path.split("/").pop() || "";
    const letter = filename.split(".")[0].toUpperCase();
    options.push({
      label: CLEAN_BACKGROUND_LABELS[filename] || `Background ${letter}`,
      value: path,
    });
  });

  return options;
}

// Legacy exports for backwards compatibility during migration
// These will be removed after all components are updated

/**
 * @deprecated Use getAdvertImages() instead
 */
export const PREDEFINED_ADVERT_IMAGES = SHARED_ADVERT_IMAGES;

/**
 * @deprecated Use getAdvertImages() instead
 */
export function getPredefinedAdvertImagePaths(): string[] {
  return getAdvertImages();
}

/**
 * @deprecated Use getProfileImages() instead
 * Returns old format paths for backwards compatibility
 */

/**
 * Base URL for serving static assets (backgrounds, adverts, etc.)
 * This is used to convert relative paths to full URLs for mobile app compatibility
 */
export const ASSET_BASE_URL = "https://dashboard.hellonoticeboard.com";

/** MIME type from a static asset path extension. */
export function mimeTypeFromAssetPath(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "webp") return "image/webp";
  if (ext === "gif") return "image/gif";
  if (ext === "svg") return "image/svg+xml";
  return "image/png";
}

/**
 * Picker/grid thumbnail for dashboard static assets.
 * Full original stays at `url`; thumb lives at `{dir}/thumbs/{stem}.webp`.
 * User uploads and non-asset URLs are returned unchanged.
 */
export function getSystemAssetThumbnail(url: string): string {
  if (!url) return url;

  let pathname = url;
  let origin = "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      pathname = parsed.pathname;
      origin = `${parsed.origin}`;
    } catch {
      return url;
    }
  }

  if (!pathname.startsWith("/assets/") || pathname.includes("/thumbs/")) {
    return url;
  }

  const lastSlash = pathname.lastIndexOf("/");
  const dir = pathname.slice(0, lastSlash);
  const file = pathname.slice(lastSlash + 1);
  const stem = file.replace(/\.[^.]+$/, "");
  if (!stem) return url;

  const thumbPath = `${dir}/thumbs/${stem}.webp`;
  return origin ? `${origin}${thumbPath}` : thumbPath;
}

/**
 * If a picker thumb 404s, fall back to the full original once.
 */
export function handleSystemAssetThumbError(
  event: { currentTarget: HTMLImageElement },
  originalUrl: string,
): void {
  const img = event.currentTarget;
  if (img.dataset.full === "1") return;
  img.dataset.full = "1";
  img.src = originalUrl;
}

/**
 * Convert a relative asset path to a full URL
 * If the path is already a full URL (starts with http:// or https://), returns it as-is
 * Otherwise, prepends ASSET_BASE_URL
 *
 * @param path - Relative path like "/assets/school/adverts/ballet.png" or full URL
 * @returns Full URL like "https://dashboard.hellonoticeboard.com/assets/school/adverts/ballet.png"
 */
export function convertAssetPathToUrl(
  path: string | undefined | null,
): string | undefined {
  if (!path || path.trim() === "") {
    return undefined;
  }

  const trimmedPath = path.trim();

  // If it's already a full URL, return as-is
  if (trimmedPath.startsWith("http://") || trimmedPath.startsWith("https://")) {
    return trimmedPath;
  }

  // If it starts with /, it's a relative path - prepend base URL
  if (trimmedPath.startsWith("/")) {
    return `${ASSET_BASE_URL}${trimmedPath}`;
  }

  // If it doesn't start with /, assume it's a relative path and add /
  return `${ASSET_BASE_URL}/${trimmedPath}`;
}
